import { Meal } from 'fitbeat.models';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import { UserDataConsumer } from '../../utils';
import {
  Text,
  View,
  StyleSheet,
  ScrollView,
  Dimensions,
  TouchableOpacity,
  StatusBar,
} from 'react-native';
import Theme from '../../theme';
import MealDetailedViewTabNavigator from './MealDetailedViewTabNavigator'
import { mealDetailTabSelected, getMaximumMealPrepTimeFromLocalStorage } from '../actions';
import { StatusBarComponent } from '../../ui/components/StatusBar';
import { defaultScale } from '../../utils/scaling';
import MealDetailMacros from './MealDetailMacros';
import CheckCircleGreenIcon from '../../assets/images/check-circle-green.svg'
import { BackgroundImageHeader } from '../../Bookings/components';
const { width } = Dimensions.get('window');
const isMobile = width <= 550;

interface IProps {
  shoppingListServingCount: number;
  meal: Meal;
  // todo: add missing fields
}

interface IState {
  imageWidth: number;
  imageHeight: number;
  currentTab: number;
  updated: boolean;
}

class MealDetailedView extends Component<IProps, IState> {
  state = {
    imageWidth: 0,
    imageHeight: 0,
    currentTab: 0,
    updated: false,
  };

  public setActiveTab = (index, selectedTab) => {
    this.setState({ currentTab: index });
    this.props.mealDetailTabSelected(selectedTab);
  };

  public componentDidMount() {
    this.props.getMaximumMealPrepTimeFromLocalStorage();
  }

  public onLayout = (event) => {
    var { width } = event.nativeEvent.layout;
    const aspectRatioMobile = 600 / 400;
    const aspectRatioFlatDesktopImage = 2400 / 1100;

    this.setState({
      imageWidth: width,
      imageHeight: width / (isMobile ? aspectRatioMobile : aspectRatioFlatDesktopImage),
    })
  }

  public render() {
    const {
      t,
      meal,
      shoppingListServingCount,
      closeMealModalAndOpenDieticianModal,
      modalTitle,
      closeModal,
      toggleMealSelectionWizardScreen,
      mealPlanChangeLoading,
      mealChanged,
    } = this.props;
    const {
      imageWidth,
      imageHeight,
      currentTab,
      updated,
    } = this.state;

    return (
      <ScrollView
        style={styles.container}
        contentContainerStyle={{ flexGrow: 1 }}
        showsHorizontalScrollIndicator={false}
      >
        <StatusBarComponent barStyle='light-content'/>
        <View onLayout={this.onLayout}>
          <BackgroundImageHeader
            imageSource={{uri: meal.urlForDesktopImage}}
            containerStyle={{ width: imageWidth, height: imageHeight}}
            contentContainerStyle={styles.imageContentContainerStyle}
            gradientOpacity = {0.6}
            gradientWebClassName='meal-selection-card-gradient'
            gradientHeight = '40%'
            useGradient={true}
            onClose={closeModal}
            actions ={
              <View  style={{
                flexDirection: 'row',
              }}>
                <View style={{flex: 1, justifyContent: 'center'}}>
                  <Text style={styles.imageHeaderText}>{modalTitle}</Text>
                </View>
                <TouchableOpacity
                  style={[styles.changeMealButton ,
                      {backgroundColor: !mealPlanChangeLoading ? Theme.PRIMARY : Theme.GREY}]}
                  onPress={toggleMealSelectionWizardScreen}
                  disabled={mealPlanChangeLoading}
                >
                  <Text style={styles.changeMealText}>
                    { !mealPlanChangeLoading ? t('changeMeal') : t('changingMeal')}
                  </Text>
                </TouchableOpacity>
              </View>
            }
          />
        </View>
        {(!mealPlanChangeLoading && mealChanged) && <View style={styles.mealPlanUpdatedContainer}>
          <CheckCircleGreenIcon stroke={Theme.GREEN} width={30} height={30} />
          <Text style={styles.mealPlanUpdatedText}>{t('mealPlanUpdated')}</Text>
        </View>}
        <View style={styles.headingAndMealCount}>
          {!isMobile && (
            <MealDetailMacros
              meal={meal}
              customMainContainerStyle={{ 
                width: '100%', 
                flex: 'unset',
              }}
              layoutStyle='meals'
            />
          )}
        </View>
        <Text style={styles.headerText}>{meal.displayName}</Text>
        <View style={styles.prepTimeTextContainer}>
          <Text style={styles.prepTimeText}>{t('prepTime')}</Text>
          <Text style={styles.prepTimeText}>: </Text>
          <Text style={styles.prepTime}>{t('prepMins', { mins: meal.prepDuration })}</Text>
        </View>
        {isMobile && (
          <MealDetailMacros 
            meal={meal} 
            customMainContainerStyle={{
              maxHeight: defaultScale(60),
            }} 
            layoutStyle='meals' 
          />
        )}
        <MealDetailedViewTabNavigator
          t={t}
          currentTab={currentTab}
          setActiveTab={this.setActiveTab}
          ingredients={meal.ingredients}
          mealIngredients={meal.mealIngredients}
          instructions={meal.instructions}
          tips={meal.tips}
          shoppingListServingCount={shoppingListServingCount}
          closeMealModalAndOpenSettings={closeMealModalAndOpenDieticianModal}
        />
      </ScrollView>
    );
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  headingAndMealCount: {
    flexDirection: 'row',
    paddingHorizontal: 0,
    zIndex: 10,
    paddingBottom: defaultScale(10),
  },
  mealPlanUpdatedContainer: {
    flex: 1,
    height: defaultScale(50),
    flexDirection:'row',
    backgroundColor: Theme.GREY_FIGMA_LIGHT,
    justifyContent: 'center',
    alignItems:'center'
  },
  mealPlanUpdatedText: {
    ...Theme.textStyle.heading2,
    paddingLeft: defaultScale(5),
  },
  headerText: {
    ...Theme.textStyle.heading,
    marginHorizontal: defaultScale(10),
  },
  prepTimeTextContainer:{
    flexDirection: 'row',
    marginHorizontal: defaultScale(10),
    paddingVertical: defaultScale(5),
  },
  prepTimeText:{
    ...Theme.textStyle.small,
  },
  prepTime:{
    ...Theme.textStyle.smallBold,
  },
  loading: {
    flex: 1,
    justifyContent: 'center',
  },
  dropdownContainer: {
    zIndex: 10,
    position: 'relative',
    paddingHorizontal: defaultScale(10),
  },
  imageHeaderText: {
    ...Theme.textStyle.heading,
    paddingLeft: defaultScale(10),
    color: Theme.WHITE,
    width: defaultScale(240),
  },
  changeMealButton: {
    height: defaultScale(45),
    width: defaultScale(117),
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'flex-end',
  },
  imageContentContainerStyle: {
    marginHorizontal: 0,
    marginVertical: 0,
    paddingBottom: 0,
    flex: 1,
  },
  changeMealText: {
    ...Theme.textStyle.bodyCopyBold,
    color: Theme.WHITE,
  },
});

const mapStateToProps = (state) => {
  const { nutrition } = state;
  const { images, mealsForTimeOfDay, isLoadingMealsForTimeOfDay, mealPlanChangeLoading, mealChanged } = nutrition;
  return {
    images,
    mealsForTimeOfDay,
    isLoading: isLoadingMealsForTimeOfDay,
    mealPlanChangeLoading,
    mealChanged,
  };
}

const mapDispatchToProps = (dispatch) => {
  return {
    mealDetailTabSelected: (selectedTab) => dispatch(mealDetailTabSelected(selectedTab)),
    getMaximumMealPrepTimeFromLocalStorage: () => dispatch(getMaximumMealPrepTimeFromLocalStorage()),
  };
};

MealDetailedView = UserDataConsumer(MealDetailedView);
MealDetailedView = connect(mapStateToProps, mapDispatchToProps)(MealDetailedView);
export default translate(['nutrition'], { wait: true })(MealDetailedView);
